import { SupervisionTimeDuties } from "@/models/supervisionTimeDuties";
import { minutesToTimeFormat } from "@/utils/dateUtils";
import { groupBy } from "@/utils/groupByUtils";

export const getDutiesOnDayInHourFormat = (
  employeeDuties: SupervisionTimeDuties[],
  dayIndex: number,
  weekCycleIndex: number
) => {
  const listOfTimesFrom: string[] = [];
  const listOfTimesTo: string[] = [];
  const dutiesOnDay = employeeDuties.filter(
    (d) => d.day_of_week === dayIndex + 1 && (d as any).week_cycle_index === weekCycleIndex
  );

  dutiesOnDay.forEach((d) => {
    listOfTimesFrom.push((d as any).from ?? "");
    listOfTimesTo.push((d as any).to ?? "");
  });
  return { dutiesOnDay: dutiesOnDay, dutiesFrom: listOfTimesFrom, dutiesTo: listOfTimesTo };
};

export const dutyDuration = (dayStart: number | null, dayEnd: number | null): number => {
  if (dayStart === 0 && dayEnd === 1440) {
    return 0;
  }
  if (dayStart && dayEnd) {
    return dayEnd - dayStart;
  } else if (dayStart === 0 && dayEnd) {
    return dayEnd;
  } else if (dayStart && dayEnd === 0) {
    return 1440 - dayStart; //1440 => 24:00
  } else {
    return 0;
  }
};

export const timeIsValid = (from: number | null, to: number | null) => {
  if (from === null || to === null) {
    return false;
  }
  if ((from === 0 && to === 0) || (from === 0 && to === 1440)) {
    return false;
  } else if (to < from && from > 0) {
    return false;
  } else {
    return true;
  }
};

export const compare = (a: SupervisionTimeDuties, b: SupervisionTimeDuties) => {
  let aStartTime = 1440; // 24:00
  let bStartTime = 1440; // 24:00
  if (a.start_time) {
    aStartTime = a.start_time;
  }
  if (b.start_time) {
    bStartTime = b.start_time;
  }
  if (aStartTime < bStartTime) {
    return -1;
  }
  if (aStartTime > bStartTime) {
    return 1;
  }
  return 0;
};

export const getWeekHours = (duties: SupervisionTimeDuties[]): string => {
  // Group duties by day of week (regardless of week_cycle_index)
  const dutiesByDayOfWeek = groupBy(duties, (duty: SupervisionTimeDuties) => duty.day_of_week);

  let weekMinutes = 0;

  dutiesByDayOfWeek.forEach((dayDuties: SupervisionTimeDuties[]) => {
    // Group duties by week_cycle_index within the same day
    const dutiesByWeekCycle = groupBy(
      dayDuties,
      (duty: SupervisionTimeDuties) => (duty as any).week_cycle_index ?? 1
    );

    // Calculate total minutes for each week cycle occurrence of this day
    const weekCycleTotals = dutiesByWeekCycle.map((cycleDuties: SupervisionTimeDuties[]) => {
      return cycleDuties.reduce(
        (total, duty) => total + dutyDuration(duty.start_time, duty.end_time),
        0
      );
    });

    // Calculate average if there are multiple occurrences, otherwise use the single occurrence
    const averageMinutesForDay =
      weekCycleTotals.reduce((sum, total) => sum + total, 0) / weekCycleTotals.length;

    weekMinutes += averageMinutesForDay;
  });

  return minutesToTimeFormat(Math.round(weekMinutes), true);
};

export const dayOfWeekToTranslationKey = (dayOfWeek: number): string => {
  const dayNames = [
    "",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday"
  ];
  return dayNames[dayOfWeek] || "";
};

export const setRowIndexOnDuties = (duties: SupervisionTimeDuties[]) => {
  if (duties.length === 0) return 0;
  // Group by day + week_cycle
  const dutiesByDay = groupBy(
    duties,
    (duty: any) => `${duty.day_of_week}-${(duty as any).week_cycle_index ?? 1}`
  ).map((d: any[]) => {
    const first = d[0] as any;
    return {
      dayOfWeek: first.day_of_week,
      weekCycleIndex: first.week_cycle_index ?? 1,
      duties: d as SupervisionTimeDuties[]
    };
  });

  // Sort duties within each day by time
  dutiesByDay.forEach((dayGroup) => {
    dayGroup.duties = dayGroup.duties.sort((a, b) => compare(a, b));
  });

  // Track index assignments: Map<"dept_id-slot", index>
  const slotIndexMap = new Map<string, number>();
  let nextIndex = 0;

  dutiesByDay.forEach((dayGroup) => {
    // Reset slot counter for each day to track position within day
    const dayDeptSlot = new Map<number, number>();

    dayGroup.duties.forEach((duty) => {
      const deptId = duty.department_id ?? -1;

      // Determine which slot this is for this department on this day
      const currentSlot = dayDeptSlot.get(deptId) ?? 0;
      dayDeptSlot.set(deptId, currentSlot + 1);

      // Create a key for this department-slot combination
      const slotKey = `${deptId}-${currentSlot}`;

      // If we've seen this department-slot before, reuse its index
      if (slotIndexMap.has(slotKey)) {
        duty.index = slotIndexMap.get(slotKey)!;
      } else {
        // Assign new index for this department-slot combination
        duty.index = nextIndex;
        slotIndexMap.set(slotKey, nextIndex);
        nextIndex++;
      }
    });
  });

  // Find max index assigned
  const max = Math.max(...duties.map(d => d.index), 0) + 1;

  return max;
};
